<template>
  <div>
    <!-- MAIN-HEADER -->
    <header :class="$style.mainHeader">
      <NavBurger
        :is-nav-hidden="!layoutStore.bars.navigation.visible"
        @click="layoutStore.toggleBarVisibility('navigation')"
      />
      <UserNav :class="$style.userNav" />
      <div :class="[$style.navigation, { [$style.lessGap]: isSymbolAndTextHeaderMode }]">
        <RouterLink v-slot="homeLink" :to="{ name: Routes.HOME }" custom>
          <SCATopNavigationItem
            as="a"
            :href="homeLink.href"
            :label="showHeaderLabel ? $t('common.label.home') : undefined"
            :icon="showHeaderIcon ? 'phosphor/house' : undefined"
            :view-mode="layoutStore.headerViewMode"
            :selected="route.name === Routes.HOME"
            :aria-label="$t('common.label.home')"
            :title="$t('common.label.home')"
            appearance="inverted"
            @click="homeLink.navigate"
          />
        </RouterLink>
        <RouterLink
          v-for="module in preFilteredModules"
          :key="`base-layout-header-module-router-link-${module.id}`"
          v-slot="link"
          :to="{ name: Routes.MODULE, params: { moduleId: module.id }, query: { tenant: workspace.tenantId } }"
          custom
        >
          <SCATopNavigationItem
            :key="`base-layout-header-module-navigation-item-${module.id}`"
            as="a"
            :href="link.href"
            :label="showHeaderLabel ? headerModuleLabel(module.builtinModule) : undefined"
            :icon="showHeaderIcon ? getModuleIcon(module.builtinModule) : undefined"
            :view-mode="layoutStore.headerViewMode"
            :selected="isModuleSelected(module.id, link.isActive)"
            :action-icon-active="isTemplateSelectorOpen(module.id)"
            :active="isTemplateSelectorOpen(module.id)"
            :aria-label="localizeBuiltinModule(module.builtinModule)"
            :title="localizeBuiltinModule(module.builtinModule)"
            appearance="inverted"
            @click="link.navigate"
          >
            <template #actionIcon>
              <TemplateSelector
                v-if="canCreateRecordsByModuleTag(module.builtinModule)"
                v-model:is-open="openTemplateSelectorId[module.id]"
                :show-all-templates="true"
                :module-id="module.id"
                :offset-y="12.5"
                :offset-x="templateSelectorOffsetX"
              >
                <SCASvgIcon icon="phosphor/caret-down" />
              </TemplateSelector>
            </template>
          </SCATopNavigationItem>
        </RouterLink>
      </div>
      <div :class="$style.logoContainer">
        <SCALoader v-if="isBusy" type="dots" appearance="white" :size="isMobile ? 'small' : 'medium'" />
        <SCALogo variant="mark" :class="[$style.logo, $style.markOnly]" />
        <SCALogo variant="wordmark" :class="[$style.logo, $style.wordmarkOnly]" />
      </div>
    </header>

    <!-- SUB-HEADER -->
    <header ref="subHeaderRef" :class="$style.subHeader">
      <div :class="$style.tenantOrgaSelect">
        <TenantOrgaSelect />
      </div>
      <div :class="$style.filtersAndBreadcrumbs">
        <div id="baseLayoutSubHeaderFiltersTeleportSlot" :class="$style.baseLayoutSubHeaderFiltersTeleportSlot"></div>
        <BreadcrumbsNav />
      </div>
      <div :class="$style.sidebarToggler">
        <div v-if="hasRightBar">
          <template v-for="(bar, key) in layoutStore.bars" :key="key">
            <SCAIconButton
              v-if="bar.showTogglerInSubHeader"
              size="small"
              :icon="sideBarIcon"
              :title="bar.label"
              :type="'toggle'"
              :icon-mirror-x="true"
              :appearance="bar.visible ? 'primary' : 'secondary'"
              :selected="bar.visible"
              @click="
                (event: MouseEvent) => {
                  layoutStore.toggleBarVisibility(key as LayoutBarId);
                  (event.currentTarget as HTMLElement)?.blur();
                }
              "
              @mouseenter="isSideBarHovered = true"
              @mouseleave="isSideBarHovered = false"
              @focus="isSideBarFocused = true"
              @blur="isSideBarFocused = false"
            />
          </template>
        </div>
        <div v-if="hasPreviewBar && !isMobile">
          <template v-for="(bar, key) in layoutStore.bars" :key="key">
            <SCAIconButton
              v-if="bar.showPreviewInSubHeader"
              size="small"
              icon="phosphor/file-magnifying-glass"
              :title="bar.label"
              :type="'toggle'"
              :appearance="bar.visible ? 'primary' : 'secondary'"
              :selected="bar.visible"
              @click="
                (event: MouseEvent) => {
                  layoutStore.toggleBarVisibility(key as LayoutBarId);
                  (event.currentTarget as HTMLElement)?.blur();
                }
              "
            />
          </template>
        </div>
      </div>
    </header>

    <div :class="$style.root">
      <SCASplitter ref="splitterRef" @resize="handleResize">
        <SCASplitterPanel
          id="navigation"
          :visible="layoutStore.bars.navigation.visible"
          :size="isMiniNavbar ? 44 : layoutStore.bars.navigation.size"
          :min-size="isMiniNavbar ? 44 : layoutStore.bars.navigation.minSize"
          :max-size="isMiniNavbar ? 44 : layoutStore.bars.navigation.maxSize"
          :is-overlay="layoutStore.bars.navigation.visible && !isDesktop && !isMiniNavbar"
          :order="0"
          :class="[$style.panel, $style.sidebarNav, { [$style.miniNavbar]: isMiniNavbar }]"
          :is-transitioning="isDesktop && !isMiniNavbar"
        >
          <SidebarNav
            :all-module-nodes="allModuleNodes"
            :favorite-module-nodes="favoriteModuleNodes"
            :bin-nav="binNav"
          />
        </SCASplitterPanel>

        <SCASplitterResizer
          v-if="(!isMiniNavbar && isDesktop) || (isMiniNavbar && layoutStore.bars.navigation.visible)"
          left-panel-id="navigation"
          right-panel-id="viewBar"
          :disabled="isMiniNavbar"
        />

        <SCASplitterPanel
          id="viewBar"
          :visible="!isMobile || !detailsVisible"
          :size="detailsVisible ? layoutStore.bars.viewBar.size : undefined"
          :min-size="layoutStore.bars.viewBar.minSize"
          :order="1"
          collapsible
          :class="[$style.panel, $style.content]"
        >
          <RouterView name="tableLayoutColumn">
            <template #default="{ Component }">
              <component :is="Component" v-if="Component" :key="tableColumnViewKey(Component)" />
            </template>
          </RouterView>
        </SCASplitterPanel>

        <SCASplitterResizer v-if="detailsVisible && !isMobile" left-panel-id="viewBar" right-panel-id="detailsBar" />

        <SCASplitterPanel
          id="detailsBar"
          :visible="detailsVisible"
          :size="layoutStore.bars.detailsBar.size"
          :min-size="layoutStore.bars.detailsBar.minSize"
          :order="2"
          :class="[$style.panel, $style.content]"
        >
          <RouterView name="detailsLayoutColumn">
            <template #default="{ Component }">
              <component :is="Component" v-if="workspace.workspaceReady" />
            </template>
          </RouterView>
        </SCASplitterPanel>

        <SCASplitterResizer
          v-if="detailsVisible && hasRightBar && layoutStore.bars.sideBar.visible && isAtLeastXL"
          left-panel-id="detailsBar"
          right-panel-id="sideBar"
        />

        <!-- List-less pages (no detailsLayoutColumn, e.g. a fixed settings page) still want a working
             sideBar — without this, sideBar has no resizer to negotiate its width against and ends up
             squeezed against viewBar. -->
        <SCASplitterResizer
          v-if="!detailsVisible && hasRightBar && layoutStore.bars.sideBar.visible && isAtLeastXL"
          left-panel-id="viewBar"
          right-panel-id="sideBar"
        />

        <SCASplitterPanel
          id="sideBar"
          :visible="hasRightBar && layoutStore.bars.sideBar.visible"
          :size="layoutStore.bars.sideBar.size"
          :min-size="layoutStore.bars.sideBar.minSize"
          :max-size="layoutStore.bars.sideBar.maxSize"
          :is-overlay="layoutStore.bars.sideBar.visible && !isAtLeastXL"
          :order="3"
          :class="[$style.panel, $style.sideBar]"
        >
          <div id="sideBarTeleport" :style="{ height: '100%' }"></div>
        </SCASplitterPanel>

        <SCASplitterResizer
          v-if="hasPreviewBar && (hasRightBar || detailsVisible) && !isMobile"
          left-panel-id="detailsBar"
          right-panel-id="previewBar"
        />

        <SCASplitterPanel
          v-if="layoutStore.bars.previewBar.visible"
          id="previewBar"
          :visible="hasPreviewBar && layoutStore.bars.previewBar.visible"
          :size="layoutStore.bars.previewBar.size"
          :min-size="layoutStore.bars.previewBar.minSize"
          :max-size="layoutStore.bars.previewBar.maxSize"
          :is-overlay="layoutStore.bars.previewBar.visible && isMobile"
          :order="4"
          :class="[$style.panel, $style.content]"
        >
          <FilePreview
            :file-url="previewFileUrl"
            :file-type="previewFileType"
            :file-name="previewFileName"
            :is-overlay="layoutStore.bars.previewBar.visible && isMobile"
            :loading="previewLoading"
            :error-message="previewErrorMessage"
            expandable
            @close="layoutStore.bars.previewBar.visible = false"
            @expand="expandPreview"
          />
        </SCASplitterPanel>
      </SCASplitter>

      <SCADetailsTooltip
        :visible="tooltipStore.visible"
        :content="tooltipStore.content"
        :target-rect="tooltipStore.targetRect"
        :placement="tooltipStore.placement"
        :loading="tooltipStore.loading"
        :avatar-type="tooltipStore.avatarType"
        :instant="tooltipStore.instant"
        :on-mouse-enter="() => tooltipStore.setHoveredOnPopover(true)"
        :on-mouse-leave="() => tooltipStore.setHoveredOnPopover(false)"
        :on-navigate="tooltipStore.navigateToCurrentRecord"
        :href="tooltipStore.recordUrl"
      />

      <div id="tooltipTeleport"></div>

      <!-- File upload / AI processing progress, plus any other long-running operation (tenant
           copy, etc.), lives here so it outlives the dialog that started it and survives
           navigation between modules (#3146). -->
      <GlobalProgressToast />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { BuiltinModuleTag } from "@scayla/api-client-fetch";
import { RecordBucket } from "@scayla/api-client-fetch";
import type { SplitterPanel } from "@scayla/components";
import {
  SCADetailsTooltip,
  SCAIconButton,
  SCALoader,
  SCALogo,
  SCASplitter,
  SCASplitterPanel,
  SCASplitterResizer,
  SCASvgIcon,
  SCATopNavigationItem,
} from "@scayla/components";
import { useScaBreakpoints } from "@scayla/core";
import { useIsFetching, useIsMutating } from "@tanstack/vue-query";
import { useDebounceFn } from "@vueuse/core";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRoute } from "vue-router";

import BreadcrumbsNav from "@/components/navigation/BreadcrumbsNav.vue";
import NavBurger from "@/components/navigation/NavBurger.vue";
import SidebarNav from "@/components/navigation/SidebarNav.vue";
import TenantOrgaSelect from "@/components/navigation/TenantOrgaSelect.vue";
import UserNav from "@/components/navigation/UserNav.vue";
import GlobalProgressToast from "@/components/progress/GlobalProgressToast.vue";
import { RECORD_DELETE_RESTORE_MUTATION_KEY } from "@/constants/mutation";
import FilePreview from "@/domains/files/components/filePreview/FilePreview.vue";
import { useModuleNav } from "@/domains/records/composables/useModuleNav";
import { localizeBuiltinModule, localizeBuiltinModuleCompact } from "@/i18n/helpers";
import { usePreviewBarFile } from "@/layouts/composables/usePreviewBarFile";
import useResponsiveBarVisibility from "@/layouts/composables/useResponsiveBarVisibility";
import { useTenantNotFoundRecovery } from "@/layouts/composables/useTenantNotFoundRecovery";
import { tableColumnViewKey } from "@/layouts/helpers/tableColumnViewKey";
import { Routes } from "@/router";
import type { LayoutBarId } from "@/store/layoutStore";
import { DEFAULT_NAVIGATION_SIZE, LAYOUT_STORE_VERSION, useLayoutStore } from "@/store/layoutStore";
import { useTooltipStore } from "@/store/tooltipStore";
import { useWorkspace } from "@/store/workspace";
import { canCreateRecordsByModuleTag, getModuleIcon } from "@/utils/moduleUtils";
import TemplateSelector from "@/views/record/TemplateSelector.vue";

const { t: $t } = useI18n();
const workspace = useWorkspace();
useTenantNotFoundRecovery();
const layoutStore = useLayoutStore();
const {
  fileUrl: previewFileUrl,
  fileType: previewFileType,
  fileName: previewFileName,
  loading: previewLoading,
  errorMessage: previewErrorMessage,
  expandPreview,
} = usePreviewBarFile();
const tooltipStore = useTooltipStore();

const route = useRoute();
const { allModuleNodes, favoriteModuleNodes, binNav } = useModuleNav();
const isFetching = useIsFetching();
// Deleting, restoring or purging a record carries every attached file with it and can run for
// seconds; ordinary saves are too short to be worth a flash of the indicator, so only the
// operations tagged with this key are counted.
const isMutating = useIsMutating({ mutationKey: RECORD_DELETE_RESTORE_MUTATION_KEY });
const isBusy = computed(() => isFetching.value > 0 || isMutating.value > 0);
const isBinRecord = computed(() => route.query.bucket === RecordBucket.DELETED);
function isModuleSelected(moduleId: string, linkActive: boolean) {
  if (isBinRecord.value) return false;
  return linkActive || (route.path.startsWith("/module/") && route.params.moduleId === moduleId);
}
const hasRightBar = computed(() => !!route.meta.rightBar);
const hasPreviewBar = computed(() => !!route.meta.previewBar);
const splitterRef = ref<InstanceType<typeof SCASplitter>>();

const isSideBarHovered = ref(false);
const isSideBarFocused = ref(false);
const openTemplateSelectorId = ref<Record<string, boolean>>({});

const isMiniNavbar = computed(() => layoutStore.sidebarNavMode === "mini");

const showHeaderIcon = computed(() => layoutStore.headerViewMode !== "text");
const showHeaderLabel = computed(() => layoutStore.headerViewMode !== "symbol");
const isSymbolAndTextHeaderMode = computed(() => layoutStore.headerViewMode === "symbolAndText");
const headerModuleLabel = (tag: BuiltinModuleTag) =>
  isSymbolAndTextHeaderMode.value ? localizeBuiltinModuleCompact(tag) : localizeBuiltinModule(tag);

const templateSelectorOffsetX = computed(() =>
  layoutStore.headerViewMode === "symbol" ? -14 : isSymbolAndTextHeaderMode.value ? -7 : -3,
);

const sideBarIcon = computed(() => {
  return layoutStore.bars.sideBar.visible || isSideBarHovered.value || isSideBarFocused.value
    ? "phosphor/layout-duotone"
    : "phosphor/layout";
});

const detailsVisible = computed(() => {
  return !!route.matched.some((record) => record.components?.detailsLayoutColumn);
});

const { isAtLeastXL, isDesktop, isMobile } = useScaBreakpoints();

useResponsiveBarVisibility("navigation", isDesktop, () => !isMiniNavbar.value);
useResponsiveBarVisibility("sideBar", isAtLeastXL);
useResponsiveBarVisibility(
  "previewBar",
  computed(() => !isMobile.value),
);

const subHeaderRef = ref<HTMLElement>();
const followsNavigationSize = computed(() => layoutStore.bars.navigation.visible && !isMiniNavbar.value);
const subHeaderNavSize = computed(() =>
  followsNavigationSize.value ? layoutStore.bars.navigation.size : DEFAULT_NAVIGATION_SIZE,
);
let appliedSubHeaderNavSize: number | undefined;

function applySubHeaderNavSize(size: number | undefined) {
  const element = subHeaderRef.value;
  if (!element || size === appliedSubHeaderNavSize) return;
  appliedSubHeaderNavSize = size;
  if (size === undefined) element.style.removeProperty("--subheader-nav-size");
  else element.style.setProperty("--subheader-nav-size", `${size}px`);
}

watch([subHeaderNavSize, subHeaderRef], () => applySubHeaderNavSize(subHeaderNavSize.value), { immediate: true });

const persistBarSizes = useDebounceFn((event: { panels: SplitterPanel[] }) => {
  for (const panel of event.panels) {
    if (panel.size === undefined) continue;
    if (panel.id === "navigation" && isMiniNavbar.value) continue;

    layoutStore.updateBarSize(panel.id as LayoutBarId, Number(panel.size));
  }
}, 100);

function handleResize(event: { panels: SplitterPanel[] }) {
  const navigationSize = event.panels.find((panel) => panel.id === "navigation")?.size;
  if (followsNavigationSize.value && navigationSize !== undefined) applySubHeaderNavSize(Number(navigationSize));
  void persistBarSizes(event);
}

function isTemplateSelectorOpen(moduleId: string): boolean {
  return !!openTemplateSelectorId.value[moduleId];
}

const preFilteredModules = computed(() => {
  return workspace.displayModules?.filter((module) => {
    return workspace.getModulePermissions(module.id).active === true;
  });
});

onMounted(() => {
  if (layoutStore.storeVersion !== LAYOUT_STORE_VERSION) {
    layoutStore.resetBars();
    layoutStore.setStoreVersion(LAYOUT_STORE_VERSION);
  }
});
</script>

<style module lang="scss">
$sidebar-mobile-width: 300px;
$panel-radius: var(--border-radius-large);

@mixin panel-shape {
  border-radius: $panel-radius $panel-radius var(--panel-bottom-radius) var(--panel-bottom-radius);
}

@mixin drawer($edge) {
  width: $sidebar-mobile-width;
  position: fixed;
  top: var(--sca-main-header-height);
  bottom: 0;
  #{$edge}: 0;
  height: auto;

  @if $edge == left {
    border-radius: 0 $panel-radius $panel-radius 0;
  } @else {
    border-radius: $panel-radius 0 0 $panel-radius;
  }
}

@mixin docked {
  position: relative;
  inset: unset;
  z-index: unset;
  height: 100%;
  width: 100%;
  @include panel-shape;
}

.mainHeader {
  display: flex;
  gap: var(--space-component-padding-medium);
  align-items: center;
  background-color: var(--color-content-brand-primary);
  min-height: var(--sca-main-header-height);
  color: var(--color-content-primary-inverted);
  padding: 0 var(--space-component-padding-medium);

  @screen xl {
    gap: var(--space-component-padding-2xLarge);
  }

  .logoContainer {
    display: flex;
    gap: var(--space-component-padding-2xLarge);
    justify-content: center;
  }

  .logo {
    height: 32px;
    color: var(--color-content-primary-inverted);
  }
  .markOnly {
    display: flex;
    @screen xl {
      display: none;
    }
  }
  .wordmarkOnly {
    display: none;
    @screen xl {
      display: flex;
    }
  }
  .userNav {
    flex-grow: 1;
    @screen xl {
      flex-grow: 0;
    }
  }
  .navigation {
    overflow: hidden;
    display: none;
    gap: var(--space-component-gap-small);
    flex-grow: 1;
    margin-left: 8px;

    &.lessGap {
      gap: var(--space-component-gap-xSmall);
    }

    @screen xl {
      display: flex;
    }
  }
}

.root {
  height: 100vh;
  height: 100dvh;
  max-height: calc(100dvh - var(--sca-header-max-height));
  padding-bottom: var(--sca-content-bottom-gap);
  width: 100vw;
  width: 100dvw;
  overflow: hidden;
  background-color: var(--color-background-neutral-onSubtle);

  // Mobile panels stay flush with the bottom of the screen.
  --panel-bottom-radius: 0px;

  @screen lg {
    --panel-bottom-radius: #{$panel-radius};
  }

  .panel {
    overflow: hidden;
    @include panel-shape;
  }

  .sidebarNav {
    @include drawer(left);
    z-index: 400;

    &.miniNavbar {
      @include docked;
      margin-left: var(--space-component-padding-small);
      width: calc(100% - var(--space-component-padding-small));
    }

    @screen 2xl {
      @include docked;
    }
  }

  .content {
    height: 100%;
    display: grid;
    grid-template-rows: auto;
  }

  .sideBar {
    @include drawer(right);
    z-index: 1000;

    @screen xl {
      @include docked;
    }
  }
}

.subHeader {
  background-color: var(--color-background-neutral-onSubtle);
  height: var(--sca-sub-header-height);
  min-width: 0;
  display: grid;
  grid-template-columns: max-content auto max-content;
  gap: var(--space-component-gap-medium);
  align-items: center;
  padding-right: var(--space-component-padding-2xLarge);

  @screen 2xl {
    grid-template-columns:
      minmax(var(--subheader-nav-size, 248px), max-content)
      auto max-content;
  }

  @media (max-width: 768px) {
    padding-right: var(--space-component-padding-small);
    gap: var(--space-component-gap-small);
  }

  .tenantOrgaSelect {
    margin: 0 0 0 var(--space-component-padding-small);
    max-width: 248px;
    min-width: 200px;
  }

  .sidebarToggler {
    display: flex;
    gap: var(--space-component-gap-small);
  }

  .baseLayoutSubHeaderFiltersTeleportSlot {
    display: flex;
    min-width: 0;
    flex: 0 1 auto;
  }
  .filtersAndBreadcrumbs {
    display: flex;
    flex-direction: column;
    gap: var(--space-component-gap-2xSmall);
    padding: 0 var(--space-component-padding-medium);
    min-width: 0;

    @screen xl {
      grid-template-columns: 24px auto;
      grid-template-rows: max-content max-content;
    }

    @media (max-width: 768px) {
      padding: unset;
    }
  }
}
</style>
